import { ClockIcon } from "@heroicons/react/20/solid"
import { IEnkCalendarViewProps } from "./calendar-types"
import { generateDaysOfMonth, getDayNames, getDayNumber } from "./utils"

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ")
}

export default function EnkMonthView(props: IEnkCalendarViewProps) {
  const { selectedDate, setSelectedDate, currentLocale } = props

  const dayNames = getDayNames(currentLocale)

  const days = generateDaysOfMonth(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate)
  //manually add events to days
  days[5].events = [
    { id: 1, name: "Design review", time: "10AM", datetime: "2022-01-03T10:00", href: "#" },
    { id: 2, name: "Sales meeting", time: "2PM", datetime: "2022-01-03T14:00", href: "#" },
  ]
  days[7].events = [{ id: 3, name: "Date night", time: "6PM", datetime: "2022-01-08T18:00", href: "#" }]

  days[12].events = [{ id: 6, name: "Sam's birthday party", time: "2PM", datetime: "2022-01-25T14:00", href: "#" }]
  days[14].events = [
    { id: 4, name: "Maple syrup museum", time: "3PM", datetime: "2022-01-22T15:00", href: "#" },
    { id: 5, name: "Hockey game", time: "7PM", datetime: "2022-01-22T19:00", href: "#" },
    { id: 9, name: "One more", time: "8PM", datetime: "2022-01-22T20:00", href: "#" },
  ]
  days[15].events = [{ id: 7, name: "Cinema with friends", time: "9PM", datetime: "2022-02-04T21:00", href: "#" }]

  const selectedDay = days.find((day) => day.isSelected)

  const handleDayClick = (day: Date) => {
    setSelectedDate(day)
  }

  return (
    <div>
      <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
          {dayNames.map((day) => (
            <div key={day} className="bg-white py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
          {/* grote weergave van hele maand met events in de cell */}
          <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
            {days.map((day) => (
              <div key={day.date} className={classNames(day.isCurrentMonth ? "bg-white" : "bg-gray-50 text-gray-500", "relative px-3 py-2")}>
                <time
                  dateTime={day.date}
                  className={day.isToday ? "flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 font-semibold text-white" : undefined}
                >
                  {getDayNumber(day)}
                </time>
                {day.events && day.events.length > 0 && (
                  <ol className="mt-2">
                    {day.events.slice(0, 2).map((event) => (
                      <li key={event.id}>
                        <a href={event.href} className="group flex">
                          <p className="flex-auto truncate font-medium text-gray-900 group-hover:text-primary">{event.name}</p>
                          <time dateTime={event.datetime} className="ml-3 hidden flex-none text-gray-500 group-hover:text-primary xl:block">
                            {event.time}
                          </time>
                        </a>
                      </li>
                    ))}
                    {day.events.length > 2 && <li className="text-gray-500">+ {day.events.length - 2} more</li>}
                  </ol>
                )}
              </div>
            ))}
          </div>
          {/* kleine weergave van dagen - events onderaan */}
          <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
            {days.map((day) => (
              <button
                key={day.date}
                type="button"
                className={classNames(
                  day.isCurrentMonth ? "bg-white" : "bg-gray-50",
                  day.isSelected || day.isToday ? "font-semibold text-white" : "",
                  (!day.isSelected && day.isCurrentMonth && !day.isToday && "text-gray-900") || "",
                  !day.isSelected && !day.isCurrentMonth && !day.isToday ? "text-gray-500" : "",
                  "flex h-14 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10"
                )}
                onClick={() => handleDayClick(new Date(day.date))}
              >
                <time
                  dateTime={day.date}
                  className={classNames(
                    day.isSelected || day.isToday ? "flex h-6 w-6 items-center justify-center rounded-full" : "",
                    !day.isSelected && day.isToday ? "bg-gray-900" : "",
                    day.isSelected ? "bg-primary" : "",
                    "ml-auto"
                  )}
                >
                  {getDayNumber(day)}
                </time>
                <span className="sr-only">{day.events ? day.events.length : 0} events</span>
                {day.events && day.events.length > 0 && (
                  <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                    {day.events.map((event) => (
                      <span key={event.id} className="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full bg-gray-400" />
                    ))}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* events onderaan de pagina -indien kleine weergave */}
      {selectedDay && selectedDay.events && selectedDay.events.length > 0 && (
        <div className="px-4 py-10 sm:px-6 lg:hidden">
          <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow ring-1 ring-black ring-opacity-5">
            {selectedDay?.events?.map((event) => (
              <li key={event.id} className="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50">
                <div className="flex-auto">
                  <p className="font-semibold text-gray-900">{event.name}</p>
                  <time dateTime={event.datetime} className="mt-2 flex items-center text-gray-700">
                    <ClockIcon className="mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                    {event.time}
                  </time>
                </div>
                <a
                  href={event.href}
                  className="ml-6 flex-none self-center rounded-md bg-white px-3 py-2 font-semibold text-gray-900 opacity-0 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400 focus:opacity-100 group-hover:opacity-100"
                >
                  Edit<span className="sr-only">, {event.name}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}
